import { forwardRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { ArrowDownRight, X } from "lucide-react";

/**
 * ExecutiveSummary — an in-page reader for the executive summary.
 * The trigger is passed via `children` (any button-shaped element).
 */
const ExecutiveSummary = forwardRef(function ExecutiveSummary({ children }, _ref) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-0 bg-ink border border-surface-2 text-ivory rounded-none [&>button]:hidden"
        data-testid="exec-summary-dialog"
      >
        <DialogHeader className="sticky top-0 z-10 bg-ink border-b border-surface-2 px-8 md:px-12 py-6 flex flex-row items-start justify-between gap-4">
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass">Executive Summary</div>
            <DialogTitle className="mt-2 font-serif-display text-ivory text-2xl md:text-3xl font-light leading-tight">
              Asad Hatimi<span className="text-brass italic">.</span> Board Practice.
            </DialogTitle>
            <DialogDescription className="mt-1 text-mutedfg text-xs uppercase tracking-[0.22em]">
              Independent Director · Enterprise Program Director
            </DialogDescription>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              data-testid="exec-summary-close"
              className="text-mutedfg hover:text-brass transition-colors"
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </DialogClose>
        </DialogHeader>

        <article className="px-8 md:px-12 py-8 md:py-10 font-sans text-ivory">
          <section className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-3">01 · Positioning</div>
            <p className="text-lg md:text-xl font-light leading-relaxed text-ivory">
              An enterprise program &amp; project management leader with{" "}
              <span className="text-brass">21+ years</span> of practice across four industrial groups —
              now offering{" "}
              <span className="italic">independent board stewardship</span> to listed companies{" "}
              <span className="text-brass">across all sectors</span>.
            </p>
          </section>

          <section className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-3">02 · Practice</div>
            <p className="text-mutedfg font-light leading-relaxed">
              Direct governance of{" "}
              <span className="text-ivory">₹3,200+ Cr in CAPEX</span> and{" "}
              <span className="text-ivory">15 commissioned programs</span> — cement, steel, power generation,
              EPC, industrial infrastructure and manufacturing. Signature disciplines: front-end loading,
              stage-gate governance, critical-chain scheduling, board MIS and statutory readiness.
            </p>
          </section>

          <section className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-3">03 · Board-Ready Committees</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              {[
                ["Audit Committee", "CWIP · cost-overrun diagnostics · vendor trueing"],
                ["Risk Management", "Constraint mapping · statutory watch · interface risk"],
                ["HSE / CSR", "PSSR · RTO · community interface"],
                ["Nomination & Governance", "Skills matrix · succession · independent challenge"],
                ["CapEx & Strategy", "Front-end loading · stage-gates · post-completion audit"],
              ].map(([n, d]) => (
                <div key={n} className="border border-surface-2 p-4">
                  <div className="font-serif-display text-ivory text-lg leading-tight">{n}</div>
                  <div className="text-xs text-mutedfg mt-1 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-3">04 · Career, at a glance</div>
            <ul className="space-y-3">
              {[
                ["Jan 2025 — Present", "PMC Head", "Pacific Iron Manufacturing Ltd. · JP Aggarwal Group"],
                ["Apr 2018 — Jan 2025", "Manager → Sr Manager → DGM", "JK Cement Ltd. · YP Singhania Group"],
                ["Oct 2007 — Mar 2018", "Sr Engineer → Assistant Manager", "UltraTech Cement Ltd. · Aditya Birla Group"],
                ["Oct 2005 — Sep 2007", "Engineer — Project Execution", "Binani Cement Ltd."],
              ].map(([p, r, c]) => (
                <li key={r} className="grid grid-cols-12 gap-3 py-3 border-b border-surface-2">
                  <span className="col-span-4 tabular text-[10px] uppercase tracking-[0.22em] text-brass">{p}</span>
                  <span className="col-span-4 text-ivory font-light text-sm">{r}</span>
                  <span className="col-span-4 text-mutedfg text-xs">{c}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-3">05 · Credentials</div>
            <p className="text-mutedfg font-light leading-relaxed">
              Executive MBA (Finance) — in progress · B.E. Mechanical, Pune University · PMP® · PMI-ACP® ·
              Professional Scrum Master I &amp; II · Critical Chain (CCPM) · Six Sigma Black / Yellow /
              Green / White · Chartered Engineer (CEng) · Fellow of the Institution of Engineers (FIE) ·
              PG Certificate, EV Design — IIT Roorkee · Leadership Program — IIM Ahmedabad ·
              Program certificates — Harvard &amp; Adelaide (edX) · Oracle Generative AI · ISC2 Cybersecurity.
            </p>
          </section>

          <section className="mb-12">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-3">06 · Sector Scope</div>
            <p className="text-mutedfg font-light leading-relaxed">
              While practice has been built in{" "}
              <span className="text-ivory">cement, steel, power generation, EPC and manufacturing</span>,
              the underlying disciplines — capital governance, program cadence, risk, HSE and independent
              challenge — travel to any listed sector where predictable delivery and shareholder value
              matter. Consulting mandates and independent directorship enquiries are welcome{" "}
              <span className="text-brass">across all sectors</span>.
            </p>
          </section>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-surface-2">
            <a
              href="/Asad_Hatimi_CV.pdf"
              download
              data-testid="exec-summary-download-cv"
              className="group inline-flex items-center gap-3 bg-brass px-6 py-3 text-ink text-[12px] uppercase tracking-[0.2em] font-semibold transition-transform duration-500 hover:-translate-y-[2px]"
            >
              Download Full Curriculum
              <ArrowDownRight size={14} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
            <a
              href="mailto:asadhatimi@gmail.com"
              data-testid="exec-summary-email"
              className="inline-flex items-center gap-2 text-mutedfg hover:text-brass link-underline text-[12px] uppercase tracking-[0.22em]"
            >
              asadhatimi@gmail.com
            </a>
          </div>
        </article>
      </DialogContent>
    </Dialog>
  );
});

export default ExecutiveSummary;
